import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router";
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from "next-themes";
import "@/i18n/config";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Contact from "@/pages/Contact";
import CartPage from "@/pages/CartPage";
import { Toaster } from "@/components/ui/toaster";
import { CartProvider } from "@/context/CartContext";
import { AdminProvider } from "@/context/AdminContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { UserProvider } from "@/context/UserContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCapture from "@/pages/admin/AdminCapture";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTranslations from "@/pages/admin/AdminTranslations";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminPages from "@/pages/admin/AdminPages";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminRedirects from "@/pages/admin/AdminRedirects";
import AdminDownloads from "@/pages/admin/AdminDownloads";
import WhatsAppButton from "@/components/WhatsAppButton";
import Analytics from "@/components/Analytics";
import ProductDetail from "@/pages/ProductDetail";
import CMSPageView from "@/pages/CMSPageView";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import VerifyEmail from "@/pages/VerifyEmail";
import NotFound from "@/pages/NotFound";
import Downloads from "@/pages/Downloads";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem forcedTheme={isAdminRoute ? 'light' : undefined}>
      <SettingsProvider>
      <AdminProvider>
      <UserProvider>
        <CartProvider>
          <ScrollToTop />
          <Analytics />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<><Navbar /><Home /><Footer /></>} />
            <Route path="/shop" element={<><Navbar /><Shop /><Footer /></>} />
            <Route path="/contact" element={<><Navbar /><Contact /><Footer /></>} />
            <Route path="/info" element={<><Navbar /><Contact /><Footer /></>} />
            <Route path="/downloads/:slug" element={<><Navbar /><Downloads /><Footer /></>} />
            <Route path="/cart" element={<><Navbar /><CartPage /><Footer /></>} />
            <Route path="/shop/:productId" element={<><Navbar /><ProductDetail /><Footer /></>} />
            <Route path="/page/:slug" element={<><Navbar /><CMSPageView /><Footer /></>} />
            <Route path="/register" element={<><Navbar /><Register /><Footer /></>} />
            <Route path="/login" element={<><Navbar /><Login /><Footer /></>} />
            <Route path="/forgot-password" element={<><Navbar /><ForgotPassword /><Footer /></>} />
            <Route path="/reset-password" element={<><Navbar /><ResetPassword /><Footer /></>} />
            <Route path="/verify-email" element={<><Navbar /><VerifyEmail /><Footer /></>} />
            <Route path="/account" element={<><Navbar /><Account /><Footer /></>} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="capture" element={<AdminCapture />} />
              <Route path="staff" element={<AdminStaff />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="translations" element={<AdminTranslations />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="brands" element={<AdminBrands />} />
              <Route path="redirects" element={<AdminRedirects />} />
              <Route path="downloads" element={<AdminDownloads />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            {/* 404 Catch-all */}
            <Route path="*" element={<><Navbar /><NotFound /><Footer /></>} />
          </Routes>
          <Toaster />
          <WhatsAppButton />
        </CartProvider>
      </UserProvider>
      </AdminProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Detect ?lang= param from URL and switch language
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    if (urlLang && ['en', 'ar'].includes(urlLang) && i18n.language?.substring(0, 2) !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
    // Set direction based on language
    const lang = i18n.language?.substring(0, 2) || 'en';
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [i18n, i18n.language]);

  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
